import useToast from "@repo/core/components/core/Toast";
import { useCreditProposalStore } from "../../store";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { MobileProposalResource } from "@repo/core/services/resources/mobile-proposal";
import { apiInstance } from "@repo/core/services/instances/api";
import {
  enrichCreditProposalSnapshotForPersist,
  mapCreditProposalToSavePayload,
  mapCreditProposalToStoreData
} from "./mappers";
import { fetchMobileConsultProposalMergedWithDetailsName } from "@repo/core/utils/proposalClientName.utils";
import {
  ConsultProposalResponseDTO,
  PROPOSTA_SITUACAO
} from "@repo/core/services/entities/Proposals";
import useAuthStore from "@repo/core/stores/auth";
import { useProposalStore } from "@repo/core/stores/proposalStore";
import { usePreProposalStore } from "@repo/core/screens/PreProposal/store/preProposalStore";
import { canContinueAuthenticatedFlow } from "@repo/core/services/session/sessionExpiration";

const proposalsResource = new ProposalsResource(apiInstance);
const mobileProposalResource = new MobileProposalResource(apiInstance);

/** Prevents concurrent duplicate POST /Proposta/Alterar for the same proposal. */
const persistInFlightByProposalId = new Map<string, Promise<boolean>>();

export const useCreditProposal = () => {
  const creditProposalStore = useCreditProposalStore();
  const toast = useToast();
  const { user } = useAuthStore();

  const getProposalData = async (lngProposta: string) => {
    try {
      const merged = await fetchMobileConsultProposalMergedWithDetailsName(
        mobileProposalResource,
        proposalsResource,
        lngProposta
      );

      const preProposalData = usePreProposalStore.getState().proposalData;
      const enrichedProposal = enrichCreditProposalSnapshotForPersist(
        merged,
        preProposalData
      );

      creditProposalStore.setProposalData(
        mapCreditProposalToStoreData(enrichedProposal)
      );
      return enrichedProposal;
    } catch (error) {
      toast.error(
        "Erro ao buscar dados da proposta",
        "Não foi possível carregar os dados da proposta. Tente novamente."
      );
      throw error;
    }
  };

  const updateProposalData = (data: Partial<ConsultProposalResponseDTO>) => {
    creditProposalStore.updateProposalData(data);
  };

  /**
   * Persists the FULL current store snapshot to the backend via Proposta/Alterar
   * with blnManterPendente=true. Used between screens so the API always reflects
   * 100% of the local state. Reads the snapshot via getState() to ensure the
   * post-updateProposalData value is sent (no closure staleness).
   *
   * Best-effort sync between wizard steps. Local store remains the source of truth;
   * failures must not block advancing when the current step form is valid.
   */
  const persistFullProposalSnapshot = async (): Promise<boolean> => {
    const snapshot = useCreditProposalStore.getState().proposalData;

    if (!snapshot?.lngProposta) {
      return true;
    }

    const proposalId = String(snapshot.lngProposta);
    const inFlight = persistInFlightByProposalId.get(proposalId);
    if (inFlight) {
      try {
        await inFlight;
        return true;
      } catch (error) {
        if (!canContinueAuthenticatedFlow()) {
          throw error;
        }

        return false;
      }
    }

    const persistPromise = (async (): Promise<boolean> => {
      try {
        const latestSnapshot = useCreditProposalStore.getState().proposalData;
        if (!latestSnapshot?.lngProposta) {
          return true;
        }

        const preProposalData = usePreProposalStore.getState().proposalData;
        const enrichedSnapshot = enrichCreditProposalSnapshotForPersist(
          latestSnapshot,
          preProposalData
        );

        if (enrichedSnapshot !== latestSnapshot) {
          creditProposalStore.updateProposalData(enrichedSnapshot);
        }

        const isReanalysisFromStore =
          useCreditProposalStore.getState().isReanalysis;

        const payload = mapCreditProposalToSavePayload(
          {
            ...enrichedSnapshot,
            strDigitador: user?.tokenData.strUsuario || "",
            strUsuarioEfetivacao: user?.tokenData.strUsuario || ""
          },
          { isReanalysis: isReanalysisFromStore, asDraft: true }
        );

        await proposalsResource.editProposal(payload);
        return true;
      } catch (error) {
        if (!canContinueAuthenticatedFlow()) {
          throw error;
        }

        console.warn("Credit proposal draft persist failed:", error);
        return false;
      }
    })();

    persistInFlightByProposalId.set(proposalId, persistPromise);
    return persistPromise.finally(() => {
      if (persistInFlightByProposalId.get(proposalId) === persistPromise) {
        persistInFlightByProposalId.delete(proposalId);
      }
    });
  };

  const saveCreditProposal = async () => {
    if (!creditProposalStore.proposalData) {
      toast.error(
        "Erro ao salvar proposta",
        "Não há dados da proposta para salvar."
      );
      throw new Error("No proposal data available to save");
    }

    const payload = mapCreditProposalToSavePayload(
      {
        ...creditProposalStore.proposalData,
        strDigitador: user?.tokenData.strUsuario || "",
        strUsuarioEfetivacao: user?.tokenData.strUsuario || ""
      },
      {
        isReanalysis: creditProposalStore.isReanalysis,
        statusOverride: PROPOSTA_SITUACAO.QUEUE_ANALYSIS_AUTOMATIC
      }
    );

    const response = await proposalsResource.editProposal(payload);

    if (response.data.lngProposta) {
      toast.success(
        "Proposta salva",
        "A proposta de crédito foi salva com sucesso."
      );

      return response.data;
    }

    throw new Error("No data received from alter proposal endpoint");
  };

  const clearCreditProposal = () => {
    creditProposalStore.resetCreditProposalStore();
    useProposalStore.getState().setIsFidelityProposal(false);
  };

  return {
    ...creditProposalStore,
    getProposalData,
    updateProposalData,
    persistFullProposalSnapshot,
    saveCreditProposal,
    clearCreditProposal
  };
};
